/**
 * RunsListSection — Läufe-Liste ohne eigenes Seiten-Layout (T6b: extrahiert aus
 * dem ehemaligen `TextRunsPage.tsx`, das mit dem Tool-Umbau entfallen ist).
 * Wird als "Läufe"-Tab-Inhalt von `BulkTextToolPage` gerendert.
 *
 * Pagination ohne Server-Total (Backend liefert nur die Seite, kein `total`):
 * "hasNextPage" wird aus der Seitengröße abgeleitet (volle Seite → vermutlich mehr).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollText } from 'lucide-react'

import { EmptyState, Pagination } from '@/components/v2/ui'

import { RunCard } from './RunCard'
import { useContentRuns } from './hooks/useContentRuns'

const PAGE_SIZE = 20

export interface RunsListSectionProps {
  /** P1/B4: Basis-Pfad der Run-Detail-Links (Default = Standalone-Tool). */
  runLinkBase?: string
}

export function RunsListSection({ runLinkBase }: RunsListSectionProps = {}) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const { data, isLoading } = useContentRuns(page)

  const runs = data?.runs ?? []
  const hasNextPage = runs.length === PAGE_SIZE
  const totalPages = page + (hasNextPage ? 1 : 0)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-40 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-800" />
        ))}
      </div>
    )
  }

  if (runs.length === 0) {
    return (
      <EmptyState
        icon={<ScrollText className="h-12 w-12" />}
        title={t('channelContent.runs.empty')}
        description={t('channelContent.runs.emptyHint')}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {runs.map((run) => (
          <RunCard key={run.id} run={run} runLinkBase={runLinkBase} />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-end border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  )
}
